import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import Loader from "./components/Loader";
import TimeSeriesAnnotation from "./views/project/label/timeseries/TimeSeriesAnnotation";
import EvaluationDashboard from "./views/project/models/EvaluationDashboard";
import ModelVersionDetail from "./views/project/models/ModelVersionDetail";
import Identity from "./views/admin/tabs/Identity";
import QuotasUsage from "./views/admin/tabs/QuotasUsage";
import Organizations from "./views/admin/tabs/tenant/Organizations/Organizations";
import PlansBilling from "./views/admin/tabs/PlansBilling/PlansBilling";
import History from "./views/admin/tabs/History";
import CreatePlan from "./views/admin/tabs/PlansBilling/CreatePlan";
import { GlobalFeatureManager, GlobalResourceManager } from "./views/admin/tabs/PlansBilling/GlobalCatalogManager";
import CreateTenant from "./views/admin/tabs/tenant/Organizations/CreateTenant";
import TenantDetails from "./views/admin/tabs/tenant/TenantDetails";
import { PlanDetails } from "./views/admin/tabs/PlansBilling/PlanDetails";
import { Users } from "./views/admin/tabs/User/Users";
import { UserDetails } from "./views/admin/tabs/User/UserDetails";
import { BillingTable } from "./views/admin/tabs/Common/BillingTable";
import SuccessView from "./views/admin/redirectScreens/SuccessView";
import FailedView from "./views/admin/redirectScreens/FailedView";
import { UserProfileEdit } from "./views/admin/tabs/User/UserProfileEdit";
import Sidenav from "./views/navbar/Sidenav";
import { DevelopAppTerminalProvider } from "./views/devices/DevelopAppTerminalContext";
import DevelopAppTerminalHost from "./views/devices/DevelopAppTerminalHost";
const Hub = lazy(() => import("./views/hub/Hub"));
const ModelHubInfo = lazy(() => import("./views/hub/ModelHubInfo"));
const Login = lazy(() => import("./views/login/Login"));
const Dashboard = lazy(() => import("./views/project/Dashboard"));
const Experiments = lazy(() =>
  import("./views/project/train/Experiments")
);
const CustomComponents = lazy(() =>
  import("./views/project/custom_components/CustomComponents")
);
const PageNotFound = lazy(() => import("./components/PageNotFound"));
const Annotations = lazy(() => import("./views/project/label/annotation/Annotations"));
const Project = lazy(() => import("./views/project/Project"));
const DeepLabel = lazy(() => import("./views/project/label/DeepLabel"));
const AnnotationAnalytics = lazy(() =>
  import("./views/project/label/AnnotationAnalytics")
);
const DeepDeploy = lazy(() =>
  import("./views/project/deploy/DeepDeploy")
);
const Documentation = lazy(() => import("./views/documentation/Documentation"));
const Admin = lazy(() => import("./views/admin/Admin"));
// const AdminUserCard = lazy(() => import("./views/admin/AdminUserCard"));
// const AdminDashboard = lazy(() => import("./views/admin/AdminDashboard"));
// const Subscription = lazy(() => import("./views/admin/Subscription"));
const Logs = lazy(() => import("./views/hub/Logs"));
const Solutions = lazy(() => import("./views/solutions/Solutions"));
const BuildSolutionDetailPage = lazy(() =>
  import("./views/solutions/BuildSolutionDetailPage")
);
const TrackingOpsDashboard = lazy(() =>
  import("./views/solutions/tracking/TrackingOpsDashboard")
);
const Devices = lazy(() => import("./views/devices/Devices"));
const SolutionFactories = lazy(() =>
  import("./views/solutions/SolutionFactories")
);
const Plants = lazy(() => import("./views/solutions/Plants"));
const StationsTab = lazy(() =>
  import("./views/solutions/StationsTab")
);
const SelectingCategoryofInspection = lazy(() =>
  import(
    "./views/solutions/VisualInspection/Inspection/SelectingCategory/SelectingCategoryofInspection"
  )
);
const InspectionSummary = lazy(() =>
  import(
    "./views/solutions/VisualInspection/Inspection/SelectingCategory/InspectionSummary"
  )
);
const InspectionDetails = lazy(() =>
  import(
    "./views/solutions/VisualInspection/Inspection/InspectionDetails/InspectionDetails"
  )
);
const VisualInspectionStarted = lazy(() =>
  import(
    "./views/solutions/VisualInspection/Inspection/SelectingCategory/VisualInspectionStarted"
  )
);
const Analyticsallplants = lazy(() =>
  import(
    "./views/solutions/VisualInspection/InspectionDashboard/Analyticsallplants"
  )
);
const TabbedAnalytics = lazy(() =>
  import(
    "./views/solutions/VisualInspection/InspectionDashboard/TabbedAnalytics"
  )
);

const UserActivityPage = lazy(() => import("./views/admin/UserActivityPage"));

const SOLUTIONS_ONLY_ROLE = "solution_user";
const ADMIN_ROLES = ["system_admin", "tenant_admin"];

const COMMON_PATTERNS = [
  /^\/user-profile(?:\/|$)/,
  /^\/admin\/user-profile-edit(?:\/|$)/,
  /^\/user-activity(?:\/|$)/,
  /^\/docs(?:\/|$)/,
  /^\/restricted(?:\/|$)/,
  /^\/login(?:\/|$)/,
];

const AMBARELLA_HUB_PATH_KEY = "ambarellaHubPath";
const AMBARELLA_ALLOWED_PATTERNS = [/^\/hub(?:\/|$)/];

function isAmbarellaCustomer() {
  return window.ENV?.CUSTOMER === "ambarella";
}

function getAmbarellaHubPath() {
  const stored = sessionStorage.getItem(AMBARELLA_HUB_PATH_KEY) || "";
  const pathname = stored.split("?")[0];
  if (AMBARELLA_ALLOWED_PATTERNS.some((pattern) => pattern.test(pathname))) {
    return stored;
  }
  return "/hub";
}

const SOLUTIONS_ROLE_ALLOWED_PATTERNS = {
  owner: [
    /^\/solutions(?:\/|$)/,
    /^\/solutionfactories(?:\/|$)/,
    /^\/plants(?:\/|$)/,
    /^\/stationstab(?:\/|$)/,
    /^\/selectingcategoryofinspection(?:\/|$)/,
    /^\/inspectiondetails(?:\/|$)/,
    /^\/inspection-summary(?:\/|$)/,
    /^\/InspectionSummary(?:\/|$)/,
    /^\/visualinspectionstarted(?:\/|$)/,
    /^\/vi-dashboard(?:\/|$)/,
    /^\/vi-Dashboard(?:\/|$)/,
    /^\/hub(?:\/|$)/,
    /^\/devices(?:\/|$)/,
  ],
  executive: [
    /^\/vi-dashboard(?:\/|$)/,
    /^\/vi-Dashboard(?:\/|$)/,
  ],
  manager: [
    /^\/solutions(?:\/|$)/,
    /^\/solutionfactories(?:\/|$)/,
    /^\/plants(?:\/|$)/,
    /^\/stationstab(?:\/|$)/,
    /^\/selectingcategoryofinspection(?:\/|$)/,
    /^\/inspectiondetails(?:\/|$)/,
    /^\/inspection-summary(?:\/|$)/,
    /^\/InspectionSummary(?:\/|$)/,
    /^\/visualinspectionstarted(?:\/|$)/,
    /^\/vi-dashboard(?:\/|$)/,
    /^\/vi-Dashboard(?:\/|$)/,
  ],
  supervisor: [
    /^\/solutions(?:\/|$)/,
    /^\/solutionfactories(?:\/|$)/,
    /^\/plants(?:\/|$)/,
    /^\/stationstab(?:\/|$)/,
    /^\/selectingcategoryofinspection(?:\/|$)/,
    /^\/inspectiondetails(?:\/|$)/,
    /^\/inspection-summary(?:\/|$)/,
    /^\/InspectionSummary(?:\/|$)/,
    /^\/visualinspectionstarted(?:\/|$)/,
    /^\/vi-dashboard(?:\/|$)/,
    /^\/vi-Dashboard(?:\/|$)/,
  ],
  inspector: [
    /^\/solutions\/?$/,
    /^\/stationstab(?:\/|$)/,
    /^\/selectingcategoryofinspection(?:\/|$)/,
    /^\/inspectiondetails(?:\/|$)/,
    /^\/inspection-summary(?:\/|$)/,
    /^\/InspectionSummary(?:\/|$)/,
    /^\/visualinspectionstarted(?:\/|$)/,
  ],
  technician: [
    /^\/solutions\/?$/,
    /^\/stationstab(?:\/|$)/,
  ],
};

function userAuth() {
  try {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("jwtToken"))
      ?.split("=")[1];

    return token;
  } catch (e) {
    localStorage.clear();
    return false;
  }
}

function RedirectSolutionFactories() {
  const { solution_id } = useParams();
  return <Navigate to={`/solutions/${solution_id}`} replace />;
}

function getDefaultRouteByRole(role, solutionRole) {
  if (isAmbarellaCustomer()) return getAmbarellaHubPath();
  if (ADMIN_ROLES.includes(role)) return "/admin";
  if (role === SOLUTIONS_ONLY_ROLE) {
    if (solutionRole === "executive") return "/vi-dashboard";
    if (solutionRole === "inspector" || solutionRole === "technician") {
      return "/solutions";
    }
    // Manager / Supervisor → Inspection entry (last VI factory hierarchy when known)
    if (solutionRole === "manager" || solutionRole === "supervisor") {
      const solutionId = localStorage.getItem("solutionId");
      if (solutionId && solutionId !== "null" && solutionId !== "undefined") {
        return `/solutions/${solutionId}`;
      }
      return "/solutions";
    }
    return "/solutions";
  }
  return "/projects";
}

function canAccessCurrentPath(pathname, role, solutionRole) {
  if (isAmbarellaCustomer()) {
    if (/^\/login(?:\/|$)/.test(pathname) || /^\/restricted(?:\/|$)/.test(pathname)) return true;
    if (ADMIN_ROLES.includes(role) && /^\/admin(?:\/|$)/.test(pathname)) return true;
    return AMBARELLA_ALLOWED_PATTERNS.some((pattern) => pattern.test(pathname));
  }
  if (COMMON_PATTERNS.some((p) => p.test(pathname))) return true;
  if (!role || role !== SOLUTIONS_ONLY_ROLE) return true;
  const allowed = SOLUTIONS_ROLE_ALLOWED_PATTERNS[solutionRole] || SOLUTIONS_ROLE_ALLOWED_PATTERNS.owner;
  return allowed.some((pattern) => pattern.test(pathname));
}

function AppLayout() {

  const role = localStorage.getItem("role");
  const solutionRole = localStorage.getItem("solutionRole");
  const location = useLocation();
  const navigate = useNavigate();

  const view = location.pathname.split("/")[1];
  const canAccessPath = !userAuth() || canAccessCurrentPath(location.pathname, role, solutionRole);

  useEffect(() => {
    if (canAccessPath) return;
    navigate(getDefaultRouteByRole(role, solutionRole), { replace: true });
  }, [canAccessPath, location.pathname, navigate, role, solutionRole]);

  // Full-bleed workspaces that bring their own chrome (hide global DeepEdge top nav).
  const hideSidenavRoutes = [
    "login",
    "annotations",
    // Visual Inspection workspace — uses its own black contextual Navbar
    "selectingcategoryofinspection",
    "inspectiondetails",
    "visualinspectionstarted",
    "inspection-summary",
    "InspectionSummary",
    "InspectionDetails",
  ];
  // VI dashboards use ViNavbar only — hide Projects/Devices/Hub global chrome.
  const isViDashboardShell =
    view === "vi-dashboard" || view === "vi-Dashboard";
  const isSolutionsDashboard = /^\/solutions\/[^/]+\/dashboard\/?$/i.test(
    location.pathname
  );
  const shouldShowSidenav =
    !hideSidenavRoutes.includes(view) &&
    !isViDashboardShell &&
    !isSolutionsDashboard;
  return (
    <DevelopAppTerminalProvider>
      {shouldShowSidenav && <Sidenav />}

      <SamlHandler />
      {!canAccessPath ? (
        <Navigate to={getDefaultRouteByRole(role, solutionRole)} replace />
      ) : (
      <Routes>
        <Route
          path="/login"
          element={
            !userAuth() ? (
              <Login />
            ) : (
              <Navigate to={getDefaultRouteByRole(role, solutionRole)} replace={true} />
              //   <Navigate
              //   to={
              //     localStorage.getItem("role") === "solution"
              //       // ? "/solutions"
              //       ? `/stationstab/${userId}`
              //       : window.ENV.CUSTOMER === "ambarella"
              //       ? "/hub"
              //       : "/projects"
              //   }
              //   replace={true}
              // />
            )
          }
        />


        <Route path="/" element={<Navigate to={`/hub`} />} />

        <Route path="/projects" element={CheckRoutes(<Project />)} />
        <Route path="/projects/:projectId" element={CheckRoutes(<Dashboard />)}>
          <Route path="deepdata" element={CheckRoutes(<Dashboard />)} />
          <Route
            path="deepdata/:datasetValue"
            element={CheckRoutes(<Dashboard />)}
          />
          <Route path="deeplabel" element={CheckRoutes(<DeepLabel />)} />
          <Route path="deeplabel/analytics" element={CheckRoutes(<AnnotationAnalytics />)} />
          {/* Legacy camelCase aliases */}
          <Route path="deepLabel" element={CheckRoutes(<DeepLabel />)} />
          <Route path="deepLabel/analytics" element={CheckRoutes(<AnnotationAnalytics />)} />
          <Route path="deepdeploy" element={CheckRoutes(<DeepDeploy />)} />
          <Route
            path="deepdeploy/:architecture"
            element={CheckRoutes(<DeepDeploy />)}
          />
          <Route
            path="deepdeploy/:architecture/model-info/:modelName"
            element={CheckRoutes(<ModelVersionDetail />)}
          />
          <Route
            path="deepdeploy/:architecture/evaluation"
            element={CheckRoutes(<EvaluationDashboard />)}
          />
          <Route path="deeptrain" element={CheckRoutes(<Experiments />)} />
          <Route
            path="customcomponents"
            element={CheckRoutes(<CustomComponents />)}
          />
        </Route>
        <Route
          path="/timeseriesannotation/:dsVersionId"
          element={<TimeSeriesAnnotation />}
        />
        <Route path="/annotations" element={CheckRoutes(<Annotations />)} />
        <Route path="/docs/*" element={CheckRoutes(<Documentation />)} />
        <Route path="/user-activity" element={<UserActivityPage />} />

        <Route path="/dashboard" element={CheckRoutes(<Admin />)}></Route>
        <Route path="/admin" element={CheckRoutes(<Admin />)}>
          {/* default tab */}
          {/* <Route index element={<Navigate to="organizations" replace />} /> */}
          {/* 
        <Route path="identity" element={<Identity />} /> */}
        </Route>
        <Route path="/admin/quotas-usage" element={CheckRoutes(<QuotasUsage />)} />
        <Route path="/admin/create-plan" element={CheckRoutes(<CreatePlan />)} />
        <Route path="/admin/resources" element={CheckRoutes(<GlobalResourceManager />)} />
        <Route path="/admin/features" element={CheckRoutes(<GlobalFeatureManager />)} />
        <Route path="/admin/create-tenant" element={CheckRoutes(<CreateTenant />)} />
        <Route path="/admin/plan-details" element={CheckRoutes(<PlanDetails />)} />
        <Route path="/admin/billing-activity" element={CheckRoutes(<BillingTable />)} />
        <Route path="/admin/history" element={CheckRoutes(<History />)} />
        <Route path="/admin/success" element={<SuccessView />} />
        <Route path="/admin/failed" element={<FailedView />} />
        <Route path="/admin/billing/success" element={<SuccessView />} />
        <Route path="/admin/billing/failed" element={<FailedView />} />
        <Route path="/admin/user-profile-edit" element={<UserProfileEdit />} />
        <Route path="/user-profile" element={CheckRoutes(<UserDetails />)} />
        <Route path="/admin/tenants" element={CheckRoutes(<Organizations />)} />
        <Route path="/admin/tenant/details" element={CheckRoutes(<TenantDetails />)} />
        <Route path="/admin/users" element={CheckRoutes(<Users />)} />
        <Route path="/admin/user-details" element={CheckRoutes(<UserDetails />)} />
        <Route path="/admin/plans-billing" element={CheckRoutes(<PlansBilling />)} />

        {/* Legacy admin URL redirects (Pascal/camel → kebab-case) */}
        <Route path="/admin/CreatePlan" element={<Navigate to="/admin/create-plan" replace />} />
        <Route path="/admin/CreateTenant" element={<Navigate to="/admin/create-tenant" replace />} />
        <Route path="/admin/PlanDetails" element={<Navigate to="/admin/plan-details" replace />} />
        <Route path="/admin/BillingActivity" element={<Navigate to="/admin/billing-activity" replace />} />
        <Route path="/admin/History" element={<Navigate to="/admin/history" replace />} />
        <Route path="/admin/Users" element={<Navigate to="/admin/users" replace />} />
        <Route path="/admin/UserDetails" element={<Navigate to="/admin/user-details" replace />} />
        <Route path="/admin/plansBilling" element={<Navigate to="/admin/plans-billing" replace />} />

        <Route path="/hub" element={CheckRoutes(<Hub />)} />
        <Route
          path="/hub/:data/evaluation"
          element={CheckRoutes(<EvaluationDashboard />)}
        />
        <Route
          path="/hub/:data"
          element={CheckRoutes(<ModelHubInfo />)}
        />

        <Route
          path="/hub/model_page"
          element={CheckRoutes(<EvaluationDashboard />)}
        />

        <Route
          path="restricted"
          element={CheckRoutes(<PageNotFound access={false} />)}
        />
        <Route path="*" element={CheckRoutes(<PageNotFound />)} />
        <Route path="/solutions" element={CheckRoutes(<Solutions />)} />
        <Route
          path="/solutions/build/:solution_id"
          element={CheckRoutes(<BuildSolutionDetailPage />)}
        />
        <Route
          path="/solutions/:solution_id/dashboard"
          element={CheckRoutes(<Analyticsallplants />)}
        />
        <Route
          path="/solutions/:solution_id/ops"
          element={CheckRoutes(<TrackingOpsDashboard />)}
        />
        <Route
          path="/solutions/:solution_id"
          element={CheckRoutes(<SolutionFactories />)}
        />
        {/* Legacy path — keep Solutions nav highlight via redirect */}
        <Route
          path="/solutionfactories/:solution_id"
          element={<RedirectSolutionFactories />}
        />
        <Route path="plants/:factory_id" element={CheckRoutes(<Plants />)} />
        <Route
          path="stationstab/:line_id"
          element={CheckRoutes(<StationsTab />)}
        />
        <Route
          path="selectingcategoryofinspection/:station_id"
          element={CheckRoutes(<SelectingCategoryofInspection />)}
        />

        <Route path="/devices" element={CheckRoutes(<Devices />)} />
        <Route path="/devices/:familySlug" element={CheckRoutes(<Devices />)} />
        <Route path="/devices/:familySlug/:deviceId" element={CheckRoutes(<Devices />)} />
        {/* Visual Inspection Router paths */}
        <Route>
                  <Route
          path="/inspectiondetails/:session_id"
          element={CheckRoutes(<InspectionDetails />)}
        />
          <Route
            path="/visualinspectionstarted"
            element={CheckRoutes(<VisualInspectionStarted />)}
          />
        </Route>
        <Route
          path="/inspection-summary"
          element={CheckRoutes(<InspectionSummary />)}
        />
        <Route path="/InspectionSummary" element={CheckRoutes(<InspectionSummary />)} />
        <Route path="/vi-dashboard" element={CheckRoutes(<Analyticsallplants />)} />
        <Route path="/vi-dashboard/:plantname/product" element={CheckRoutes(<TabbedAnalytics />)} />
        <Route path="/vi-dashboard/:plantname/category" element={CheckRoutes(<TabbedAnalytics />)} />
        <Route path="/vi-dashboard/:plantname/batch" element={CheckRoutes(<TabbedAnalytics />)} />
        <Route path="/vi-dashboard/:plantname/line" element={CheckRoutes(<TabbedAnalytics />)} />
        <Route path="/vi-dashboard/:plantname/inspector" element={CheckRoutes(<TabbedAnalytics />)} />
        {/* Legacy VI URL aliases */}
        <Route path="/vi-Dashboard" element={CheckRoutes(<Analyticsallplants />)} />
        <Route path="/vi-Dashboard/:plantname/Product" element={CheckRoutes(<TabbedAnalytics />)} />
        <Route path="/vi-Dashboard/:plantname/Category" element={CheckRoutes(<TabbedAnalytics />)} />
        <Route path="/vi-Dashboard/:plantname/Batch" element={CheckRoutes(<TabbedAnalytics />)} />
        <Route path="/vi-Dashboard/:plantname/Line" element={CheckRoutes(<TabbedAnalytics />)} />
        <Route path="/vi-Dashboard/:plantname/Inspector" element={CheckRoutes(<TabbedAnalytics />)} />
      </Routes>
      )}
      <DevelopAppTerminalHost />
    </DevelopAppTerminalProvider>
  )
}

export default function Router() {

  return (
    <>
      <BrowserRouter>
        <AppLayout />
      </BrowserRouter>
    </>
  );
}
function SamlHandler() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const name = params.get('name');
    const user_id = params.get('user_id');
    const tenant_id = params.get('tenant_id');
    const privacy_policy_accepted = params.get('privacy_policy_accepted');
    const privacy_policy_version = params.get('privacy_policy_version');

    if (name && user_id) {
      console.log('SAML callback detected, setting localStorage...');

      localStorage.setItem("name", name);
      localStorage.setItem("user_id", user_id);
      localStorage.setItem("role", "developer");

      if (tenant_id) {
        localStorage.setItem("tenant_id", tenant_id);
      }
      if (privacy_policy_accepted) {
        localStorage.setItem("privacy_policy_accepted", privacy_policy_accepted);
      }
      if (privacy_policy_version) {
        localStorage.setItem("privacy_policy_version", privacy_policy_version);
      }

      console.log('SAML login completed for:', name);

      // Remove SAML params but keep other query params
      params.delete('name');
      params.delete('user_id');
      params.delete('tenant_id');
      params.delete('privacy_policy_accepted');
      params.delete('privacy_policy_version');


      // Build new URL with remaining params
      const remainingParams = params.toString();
      const newPath = remainingParams
        ? `${location.pathname}?${remainingParams}`
        : location.pathname;

      navigate(newPath, { replace: true });
    }
  }, [location, navigate]);

  return null;
}

function CheckRoutes(component) {
  return userAuth() ? (
    <Suspense> {component}</Suspense>
  ) : (
    <Navigate to="/login" replace={true} />
  );
}
